import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  TextField,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getLoanDetail,
  getChargeTypes,
  addCharge,
  processDueDate,
  applyPayment,
  type LoanDetailResponse,
  type ChargeTypeItem,
} from '../../endpoints/loans';

const LoansTestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [selectedChargeTypeId, setSelectedChargeTypeId] = useState<string>('');
  const [chargeStartDate, setChargeStartDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: loan,
    isLoading,
    error,
    refetch,
  } = useApiGet<LoanDetailResponse>(['loan', id!], () => getLoanDetail(id!), {
    enabled: !!id,
  });

  const { data: chargeTypes = [] } = useApiGet<ChargeTypeItem[]>(
    ['loan-charge-types'],
    getChargeTypes,
    { enabled: true }
  );

  const { mutate: doApplyPayment, isPending: paymentPending } = useApiSend(
    (body: { amount: number; payment_date: string; payment_method: string }) =>
      applyPayment(body),
    () => {
      setPaymentAmount('');
      setActionError(null);
      refetch();
    },
    (err: Error) =>
      setActionError((err as any)?.response?.data?.error ?? err.message),
    [['loan', id]]
  );

  const { mutate: doAddCharge, isPending: chargePending } = useApiSend(
    (body: { name: string; amount: number; start_date: string }) =>
      addCharge(id!, body),
    () => {
      setSelectedChargeTypeId('');
      setActionError(null);
      refetch();
    },
    (err: Error) =>
      setActionError((err as any)?.response?.data?.error ?? err.message),
    [['loan', id]]
  );

  const { mutate: doProcessDueDate, isPending: dueDatePending } = useApiSend(
    (body: { due_date: string }) => processDueDate(id!, body),
    () => {
      setActionError(null);
      refetch();
    },
    (err: Error) =>
      setActionError((err as any)?.response?.data?.error ?? err.message),
    [['loan', id]]
  );

  if (!id) return <Typography>Missing loan ID</Typography>;
  if (isLoading) return <CircularProgress />;
  if (error || !loan) {
    return (
      <Typography color="error">
        {(error as any)?.response?.data?.error ??
          (error as Error)?.message ??
          'Loan not found'}
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button
          size="small"
          onClick={() => navigate('/administration/loans-test')}
        >
          ← Back to list
        </Button>
        <Typography variant="h5" sx={{ mt: 1 }}>
          Loan {loan.id.slice(0, 8)}…
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Summary" />
          <CardContent>
            <Typography>User ID: {loan.user_id}</Typography>
            <Typography>
              Amount: {loan.amount.toFixed(2)} {loan.currency}
            </Typography>
            <Typography>Status: {loan.status}</Typography>
            <Typography>First payment: {loan.first_payment_date}</Typography>
            <Typography>
              Next payment: {loan.next_payment_date ?? '—'}
            </Typography>
            <Typography>
              Interest rate: {(loan.interest_rate * 100).toFixed(2)}%
            </Typography>
            <Typography>
              Principal only: {loan.principal_only ? 'Yes' : 'No'}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Current balances</Typography>
              <Typography>
                Principal: {loan.current_principal_balance.toFixed(2)}
              </Typography>
              <Typography>
                Interest: {loan.current_interest_balance.toFixed(2)}
              </Typography>
              <Typography>
                Deferred: {loan.current_deferred_balance.toFixed(2)}
              </Typography>
              <Typography>
                Charges: {loan.current_charges_balance.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Add payment" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  label="Amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="date"
                  label="Payment date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  label="Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    !paymentAmount ||
                    Number(paymentAmount) <= 0 ||
                    paymentPending
                  }
                  onClick={() =>
                    doApplyPayment({
                      amount: Number(paymentAmount),
                      payment_date: paymentDate,
                      payment_method: paymentMethod,
                    })
                  }
                >
                  {paymentPending ? 'Applying…' : 'Apply payment'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardHeader title="Add charge" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="charge-type-label">Charge type</InputLabel>
                  <Select
                    labelId="charge-type-label"
                    label="Charge type"
                    value={selectedChargeTypeId}
                    onChange={(e) =>
                      setSelectedChargeTypeId(e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>Select a charge</em>
                    </MenuItem>
                    {chargeTypes.map((ct) => (
                      <MenuItem key={ct.id} value={ct.id}>
                        {ct.name} — {ct.amount.toFixed(2)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  type="date"
                  label="Start date"
                  value={chargeStartDate}
                  onChange={(e) => setChargeStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    !selectedChargeTypeId || !chargeStartDate || chargePending
                  }
                  onClick={() => {
                    const selected = chargeTypes.find(
                      (ct) => ct.id === selectedChargeTypeId
                    );
                    if (!selected) return;
                    doAddCharge({
                      name: selected.name,
                      amount: selected.amount,
                      start_date: chargeStartDate,
                    });
                  }}
                >
                  {chargePending ? 'Adding…' : 'Add charge'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardHeader title="Process due date (accrual/anniversary)" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="date"
                  label="Due date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={dueDatePending}
                  onClick={() => doProcessDueDate({ due_date: dueDate })}
                >
                  {dueDatePending ? 'Processing…' : 'Process due date'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {actionError && (
        <Grid item xs={12}>
          <Typography color="error">{actionError}</Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Balance log history (audit)
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Log date</TableCell>
                <TableCell align="right">Principal</TableCell>
                <TableCell align="right">Interest</TableCell>
                <TableCell align="right">Deferred</TableCell>
                <TableCell align="right">Charges</TableCell>
                <TableCell align="right">New principal</TableCell>
                <TableCell align="right">New interest</TableCell>
                <TableCell align="right">New deferred</TableCell>
                <TableCell align="right">New charges</TableCell>
                <TableCell>Ref</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loan.balance_logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>No log entries yet.</TableCell>
                </TableRow>
              ) : (
                loan.balance_logs.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {new Date(log.log_date).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {log.principal_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.interest_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.deferred_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.charges_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_principal_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_interest_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_deferred_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_charges_balance.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {log.payment_allocation_id &&
                        `pay:${log.payment_allocation_id.slice(0, 6)}…`}
                      {log.charge_id && ` chg:${log.charge_id.slice(0, 6)}…`}
                      {!log.payment_allocation_id && !log.charge_id && '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default LoansTestDetail;
